import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import { selectCurrentUser } from 'ducks/currentUser/selectors/selectCurrentUser'
import { canShowError } from 'subapps/onboarding/utils'

import EmailForm from '../../sharedForm/EmailForm'
import ResendToken from '../../sharedForm/ResendToken'
import { doSaveAndRedirect } from './actions'
import useFieldFocusChange from '../hooks/useFieldFocusChange'
import { styles } from './styles'

const CONFIRMATION_TOKEN = 'confirmationToken'
const EXPECTED_CODE_LENGTH = 6

// Validation schema
const FORM_SCHEMA = yup.object().shape({
  [CONFIRMATION_TOKEN]: yup
    .string() // not using number() here as the verification code could start with a 0
    .required('Code is required')
    .trim()
    .length(EXPECTED_CODE_LENGTH, 'Invalid code')
    .matches(/^[0-9]+$/, 'Invalid code'),
})

const defaultValues = {
  [CONFIRMATION_TOKEN]: '',
}

const VerificationPage = () => {
  const dispatch = useDispatch()
  const user = useSelector(selectCurrentUser)
  const email = user.unconfirmedEmail || user.email

  const {
    register,
    handleSubmit,
    setFocus,
    setError,
    setValue,
    watch,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(FORM_SCHEMA),
  })

  useEffect(
    () => {
      setFocus(CONFIRMATION_TOKEN)
    },
    [setFocus]
  )

  const submitDisabled = !isValid || isSubmitting
  const [focusedFields, setFocusedFields] = useState(null)
  const codeProps = useFieldFocusChange(
    register(CONFIRMATION_TOKEN),
    setFocusedFields
  )
  const codeValue = watch(CONFIRMATION_TOKEN)
  const codeError =
    errors[CONFIRMATION_TOKEN] &&
    canShowError(focusedFields, CONFIRMATION_TOKEN, codeValue)
      ? errors[CONFIRMATION_TOKEN].message
      : undefined

  const onSubmit = useCallback(
    async data => {
      try {
        await dispatch(doSaveAndRedirect(data))
      } catch (err) {
        setError(CONFIRMATION_TOKEN, { message: err[CONFIRMATION_TOKEN]?.[0] })
      }
    },
    [dispatch, setError]
  )

  const handleEmailFormSubmit = useCallback(
    () => {
      setValue(CONFIRMATION_TOKEN, '', { shouldValidate: true })
      setFocus(CONFIRMATION_TOKEN)
    },
    [setValue, setFocus]
  )

  const handleEmailFormClose = useCallback(
    () => {
      setFocus(CONFIRMATION_TOKEN)
    },
    [setFocus]
  )

  const handlePaste = useCallback(
    e => {
      const value =
        (e.clipboardData || window.clipboardData).getData('text') || ''
      const parsedValue = value
        .replace(/[^0-9]/g, '')
        .substring(0, EXPECTED_CODE_LENGTH)
      setValue(CONFIRMATION_TOKEN, parsedValue, { shouldValidate: true })
      e.preventDefault()
    },
    [setValue]
  )

  return (
    <div css={styles.container} className="signupVerification">
      <h4 className="grui" css={styles.heading}>
        We need to verify your email address
      </h4>

      <div>
        A 6 digit code has been set to{' '}
        <strong css={styles.semibold}>{email}</strong> to verify your email
        address
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Field
          placeholder="Enter your 6 digit code"
          className="grui mb-4"
          css={styles.field}
          validateStatus={codeError ? 'error' : undefined}
          help={codeError}
          {...codeProps}
          onPaste={handlePaste}
          maxLength="6"
          autoComplete="off"
        />
        <div css={styles.reEmail}>
          <EmailForm
            onSubmit={handleEmailFormSubmit}
            onClose={handleEmailFormClose}
          />
          <ResendToken content="resend" />
        </div>
        <Button
          className="grui mt-11"
          htmlType="submit"
          type="info"
          size="xBig"
          css={styles.submitButton}
          disabled={submitDisabled}
        >
          Take me to my account
        </Button>
      </form>
    </div>
  )
}

export default VerificationPage
